import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Snackbar, Alert, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { tokens } from '../../theme';
import AppPagination, { pageSlice } from '../../components/common/AppPagination';

const newNote = (userId, invoiceId) => ({
    userId,
    createdDate: new Date().toISOString().split('T')[0],
    createdTime: new Date().toTimeString().split(' ')[0],
    idCompany: 1,
    invoiceId,
    invoiceNotesId: null,
    notesIndicator: 2,
    notesText: '',
});

const formatDate = (value) => {
    if (!value) return '';
    try {
        return new Date(value).toLocaleDateString();
    } catch (e) {
        return value;
    }
};

const formatTime = (value) => (value ? String(value).substring(0, 5) : '');

const SystemNotesTable = ({ notesData, invoiceId, saveNotes }) => {
    const authInfo = useSelector((state) => state.authInfo);
    const userId = (authInfo && authInfo.userId) || '';
    const [notes, setNotes] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [draft, setDraft] = useState(newNote(userId, invoiceId));
    const [draftError, setDraftError] = useState('');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (notesData && Array.isArray(notesData)) setNotes(notesData);
    }, [notesData]);

    const openAdd = () => {
        setDraft(newNote(userId, invoiceId));
        setDraftError('');
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!draft.notesText.trim()) {
            setDraftError('Write something before saving.');
            return;
        }
        setSaving(true);
        try {
            await saveNotes({ ...draft, userId: userId || draft.userId, invoiceId });
            setDialogOpen(false);
            setSaved(true);
        } catch (error) {
            setDraftError("The note couldn't be saved. Try again.");
            console.error('Error saving note:', error);
        } finally {
            setSaving(false);
        }
    };

    const term = searchTerm.toLowerCase();
    const filtered = notes.filter(
        (note) =>
            (note.notesText && note.notesText.toLowerCase().includes(term)) ||
            (note.userId && note.userId.toLowerCase().includes(term))
    );
    const visible = pageSlice(filtered, page, rowsPerPage);

    return (
        <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                    placeholder="Search notes"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPage(0);
                    }}
                    sx={{ flex: '1 1 260px', maxWidth: 420 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
                    Add note
                </Button>
            </Box>

            <Box sx={{ border: `1px solid ${tokens.line}`, borderRadius: 1.5, overflow: 'hidden' }}>
                <TableContainer>
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 200 }}>By</TableCell>
                                <TableCell sx={{ width: 170 }}>Date / time</TableCell>
                                <TableCell>Note</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visible.map((note, index) => (
                                <TableRow key={note.invoiceNotesId || index} sx={{ verticalAlign: 'top' }}>
                                    <TableCell sx={{ wordBreak: 'break-word', fontWeight: 500 }}>{note.userId || '—'}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                        {formatDate(note.createdDate)} {formatTime(note.createdTime)}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{note.notesText || '—'}</TableCell>
                                </TableRow>
                            ))}
                            {visible.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                                        {searchTerm ? 'No notes match your search.' : 'No notes on this invoice yet.'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {filtered.length > 0 && (
                    <AppPagination
                        count={filtered.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={setPage}
                        onRowsPerPageChange={(value) => {
                            setRowsPerPage(value);
                            setPage(0);
                        }}
                    />
                )}
            </Box>

            <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add a note</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={5}
                        label="Note"
                        placeholder="Visible to All City Towing dispatch"
                        value={draft.notesText}
                        onChange={(e) => {
                            setDraft({ ...draft, notesText: e.target.value });
                            setDraftError('');
                        }}
                        error={Boolean(draftError)}
                        helperText={draftError}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save note'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={saved}
                autoHideDuration={3000}
                onClose={() => setSaved(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="success" variant="filled" onClose={() => setSaved(false)}>
                    Note saved
                </Alert>
            </Snackbar>
        </>
    );
};

export default SystemNotesTable;
